import {
    replacePlaceholders,
    getFieldScheme,
    getMaxDecimalDigitsFromScheme,
    getFieldTitle,
    getRequiredFieldsBasedOnPayload,
} from './helper';
import ERROR_MESSAGE_COPIES, {ERROR_KEYWORDS, ERROR_MESSAGE_TIERS} from '@/constants/error-messages/index';
import { getConditionallyRequiredErrorMessage, getMandatoryErrorMessage } from './mandatory-error';
import { getMinMaxLengthErrorMessage, getMinMaxValueErrorMessage } from './length-error';
import { getPatternErrorMessage } from './pattern-error';

export function ajvErrorMapping(errors, schema, payload, options = { fieldLineNumbers: {}, isCSV: false, cellMap: null }, apiSpecUrl = '') {
    if (!Array.isArray(errors) || !schema) {
        return {};
    }
    const errorMessagesByFieldPath = {};
    for (const error of errors) {
        // date element payload structure error
		if (error.keyword === ERROR_KEYWORDS.ADDITIONAL_PROPERTIES) {
            const link = apiSpecUrl;
            const errorMessage = replacePlaceholders(
                ERROR_MESSAGE_COPIES.payload.structure,
                link,
            );
            const structureErr = new Error(errorMessage);
            structureErr.name = "PayloadStructureError";
            throw structureErr;

        }

        const fieldScheme = getFieldScheme(error, schema);

        let errorMessage = '';
        let errorTier = ERROR_MESSAGE_TIERS.OTHER;  
        let {instancePath} = error;

        // this is list of required fields based on payload and schema
        // this includes conditionally required fields based on the payload data
        const allRequiredFields = getRequiredFieldsBasedOnPayload(schema, payload);

        /*
         * First condition is to check if the field already has a mandatory error
         *  Among ajv errors, mandatory error has the top priority. So if the field already has it, we ignore all the other ajv errors
         * Second condition is to check if the field is not required when the value is empty
         *  In this case, we don't need to show any error.
         *  Why? Because ajv gives errors for an empty string "" when it has format and pattern 
         *  and we don't want to show this error when field value is empty and field isn't required
         */

        const fieldId = instancePath.split('/')?.length > 1 ? instancePath.split('/')[1] : instancePath;
        if (
            errorMessagesByFieldPath[instancePath]?.errorTier === ERROR_MESSAGE_TIERS.MANDATORY ||
            // Only skip validation for optional fields with empty values when it's not a type error
            (!allRequiredFields?.includes(fieldId) && !error.data && error.keyword !== ERROR_KEYWORDS.TYPE)
        ) {
            continue;
        }
        if (
            error.keyword === ERROR_KEYWORDS.REQUIRED || 
            ((error.data === '' || error.data == null) && allRequiredFields.includes(error.instancePath.slice(1)))
        ) {
            if (error.schemaPath.includes("allOf")) {
                errorMessage = getConditionallyRequiredErrorMessage(schema, error);
            } else {
                errorMessage = getMandatoryErrorMessage(fieldScheme);
            }
            errorTier = ERROR_MESSAGE_TIERS.MANDATORY;
            instancePath =
                error.keyword === ERROR_KEYWORDS.REQUIRED ?
                `${instancePath}/${error.params.missingProperty}` :
                instancePath;
        } else if (error.keyword === ERROR_KEYWORDS.TYPE) {
            if (Array.isArray(error.parentSchema?.type)) {
                errorMessage = replacePlaceholders(
                    ERROR_MESSAGE_COPIES.type,
                    error.parentSchema?.type.filter(t => t !== 'null').join(", ")
                );
            } else {
                errorMessage = replacePlaceholders(
                    ERROR_MESSAGE_COPIES.type,
                    error.parentSchema?.type
                );
            }
        } else if (
            error.keyword === ERROR_KEYWORDS.MIN_LENGTH ||
            error.keyword === ERROR_KEYWORDS.MAX_LENGTH
        ) {
            if (error.data === '' || error.data == null) {
                errorMessage = getConditionallyRequiredErrorMessage(schema, error);
                errorTier = ERROR_MESSAGE_TIERS.MANDATORY;
            } else {
                errorMessage = getMinMaxLengthErrorMessage(fieldScheme);
            }
        } else if (error.keyword === ERROR_KEYWORDS.PATTERN) {
            // When pattern error is from custom date-time format, handle it inside format error
            if (!fieldScheme?.format?.includes('custom:date-time')) {
                errorMessage = getPatternErrorMessage(fieldScheme, error.data);
            }
        } else if (error.keyword === ERROR_KEYWORDS.FORMAT) {
            if (fieldScheme?.format?.includes('custom:date-time')) {
                errorMessage = replacePlaceholders(ERROR_MESSAGE_COPIES.format["date-time"], fieldScheme.format.split(':').slice(2).join(':'), fieldScheme.example);
            } else {
                if (fieldScheme.format === 'date' || fieldScheme.format === 'date-time') {
                    errorMessage = replacePlaceholders(ERROR_MESSAGE_COPIES.format[fieldScheme.format], fieldScheme.format.split(':').slice(2).join(':'), fieldScheme.example);
                } else if (fieldScheme.format !== "int64") {
                    // Only show generic message for non-date formats that aren't int64
                    errorMessage = ERROR_MESSAGE_COPIES.generic;
                }
            }
        } else if (error.keyword === ERROR_KEYWORDS.ENUM) {
            errorMessage = replacePlaceholders(
                ERROR_MESSAGE_COPIES.enum,
                fieldScheme.enum.join(', ')
            );
        } else if (
            error.keyword === ERROR_KEYWORDS.MINIMUM ||
            error.keyword === ERROR_KEYWORDS.MAXIMUM
        ) {
            /*
             * For enum fields, if the value is not in the range of min/max,
             *  it means ajv already gave an error about invalid enum option value
             *  so we don't need to show about min/max range again
             */ 
            if (!fieldScheme.enum || !fieldScheme?.enum?.length ) {
                errorMessage = getMinMaxValueErrorMessage(fieldScheme);
            }
        } else if (error.keyword === ERROR_KEYWORDS.MULTIPLE_OF) {
            errorMessage = replacePlaceholders(
                ERROR_MESSAGE_COPIES.length.maxDecimalPlacesLength,
                getMaxDecimalDigitsFromScheme(fieldScheme.multipleOf),
            );
        } else if (error.keyword === ERROR_KEYWORDS.SUM_EQUALS) {
            errorMessage = error?.message || replacePlaceholders(ERROR_MESSAGE_COPIES.business?.sumEquals, error.params.totalExpected)
        }
        else if (error.keyword === ERROR_KEYWORDS.SAME_DAY) {
            errorMessage = replacePlaceholders(ERROR_MESSAGE_COPIES.business?.sameDay, error?.schema?.["rootField"], error?.schema?.["itemField"] || "")
        }
        else if (error.keyword === ERROR_KEYWORDS.MAX_TOTAL_DURATION) {
            errorMessage = error?.message || replacePlaceholders(ERROR_MESSAGE_COPIES.business?.maxTotalDuration, error.params.totalDuration, error.params.maxDuration)
        }
        else if (error.keyword === ERROR_KEYWORDS.DATE_COMPARE) {
            errorMessage = error?.message || replacePlaceholders(ERROR_MESSAGE_COPIES.business?.dateCompare, error.params.date1, error.params.operator, error.params.date2)
        }

        // Only process and add error messages that exist
        if (errorMessage) {
            let location;
            let cleanPath = instancePath.startsWith('/') ? instancePath.slice(1) : instancePath;
            
            // First try the exact path as provided by AJV
            let lineNumber;
            
            // Convert array notation from /array/0/field to array.0.field for dot notation
            let dotPath = cleanPath.replace(/\//g, '.');
            
            // Also create a slash path format (array/0/field)
            let slashPath = cleanPath;
            
            // Try multiple path formats to find a line number
            if (options.isCSV && options.cellMap && instancePath) {
                // For CSV files, use the cell position if available
                const cellInfo = options.cellMap[cleanPath] || options.cellMap[dotPath];
                location = cellInfo ? `Row ${cellInfo.row}${cellInfo.col ? `, column ${cellInfo.col}` : ''}` : '';
            } else {
                // For JSON files, try multiple path formats to find the line number
                lineNumber = options.fieldLineNumbers[cleanPath] || 
                             options.fieldLineNumbers[dotPath] || 
                             options.fieldLineNumbers[slashPath];
                
                // If we still don't have a line number, try alternative formats
                if (!lineNumber && cleanPath.includes('/')) {
                    // Try different path formats for nested array items
                    const pathParts = cleanPath.split('/');
                    
                    // For paths like steel_element_test_details/0/tensile_strength
                    // Try steel_element_test_details.0.tensile_strength
                    const altDotPath = pathParts.join('.');
                    lineNumber = options.fieldLineNumbers[altDotPath];
                    
                    // If still no line number, try to get the line number of the field directly
                    if (!lineNumber && pathParts.length > 2) {
                        // Try to get the line number of just the field name (e.g., tensile_strength)
                        const fieldName = pathParts[pathParts.length - 1];
                        lineNumber = options.fieldLineNumbers[fieldName];
                    }
                    
                    // If still no line number, try to get the line number of the parent object
                    if (!lineNumber && pathParts.length > 1) {
                        // Try to get the line number of the array item itself (e.g., steel_element_test_details/0)
                        const parentPath = pathParts.slice(0, 2).join('/');
                        const parentDotPath = pathParts.slice(0, 2).join('.');
                        
                        lineNumber = options.fieldLineNumbers[parentPath] || options.fieldLineNumbers[parentDotPath];
                        
                        // If still no line number, try the array itself
                        if (!lineNumber && pathParts.length > 0) {
                            lineNumber = options.fieldLineNumbers[pathParts[0]];
                        }
                    }
                }
                
                // No hardcoded line numbers - rely on dynamic detection
                
                location = lineNumber ? `Line ${lineNumber}` : '';
            }

            const fieldTitle = getFieldTitle(error, schema);
            
            // Standard error message formatting for all fields
            const messageWithPrefix = instancePath === '' ? errorMessage : 
                `${fieldTitle}${location ? `, ${location}` : ''}: ${errorMessage}`;
            
            // Initialize array if needed and add message if not already present
            if (
                errorMessagesByFieldPath[instancePath]?.errorTier === undefined ||
                errorMessagesByFieldPath[instancePath]?.errorTier > errorTier
            ) {
                errorMessagesByFieldPath[instancePath] = {
                    errorMessages: [messageWithPrefix],
                    errorTier
                };
            } else if (errorMessagesByFieldPath[instancePath]?.errorTier === errorTier) {
                errorMessagesByFieldPath[instancePath].errorMessages.push(messageWithPrefix);
            }
        }
    }
    // Flatten the errorMessagesByFieldPath to only contain arrays of messages
    const flatErrorMessages = {};
    for (const [key, value] of Object.entries(errorMessagesByFieldPath)) {
        const cleaned = key.replace(/^\/+/, ''); // remove leading slash
        flatErrorMessages[cleaned] = value.errorMessages;
    }

    return flatErrorMessages;
}

// Entry point for ajvErrorMapping utilities
export * from './helper';